import { useState } from "react";
import { Briefcase, Upload, CheckCircle2, FileText } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Jobs() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [resume, setResume] = useState(null);
  const [cover, setCover] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    if (!resume) { toast.error("Please attach your resume"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      fd.append("resume", resume);
      if (cover) fd.append("cover_letter", cover);
      Array.from(photos).slice(0, 10).forEach((p) => fd.append("photos", p));
      await api.post("/jobs/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="bg-zinc-50 min-h-screen">
        <div className="container mx-auto px-6 py-24 max-w-2xl text-center">
          <CheckCircle2 className="w-14 h-14 text-[#16A34A] mx-auto" />
          <h1 className="font-display text-4xl font-black tracking-tight mt-6" data-testid="jobs-success">Thanks — we've got it.</h1>
          <p className="text-zinc-600 mt-4">Thanks for expressing your interest in joining the team at Nomad Caravan Repairs. We'll review your details and be in touch if there's a good fit.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-zinc-50 min-h-screen">
      <section className="bg-zinc-950 text-white">
        <div className="container mx-auto px-6 py-16 max-w-3xl">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#22C55E] mb-4 flex items-center gap-2">
            <Briefcase className="w-3.5 h-3.5" /> Job Vacancy
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">Customer Service Advisor &amp; Digital Coordinator</h1>
          <p className="mt-4 text-zinc-300 font-semibold">Nomad Caravan Repairs Whitsundays — Bowen, QLD</p>
        </div>
      </section>

      <section className="container mx-auto px-6 py-12 max-w-3xl">
        <article className="bg-white border border-zinc-200 p-6 sm:p-10 space-y-8" data-testid="jobs-ad">
          <div>
            <h2 className="font-display text-2xl font-black tracking-tight">About the Role</h2>
            <p className="mt-3 text-zinc-700 leading-relaxed">We are looking for a reliable, tech-savvy local to join our team. This isn't a typical "front desk" job. You will be the vital human link between our innovative AI-driven website and the physical work floor, ensuring our customers get a superior experience while our tradespeople stay focused on the tools.</p>
          </div>

          <div>
            <h2 className="font-display text-2xl font-black tracking-tight">The Details</h2>
            <ul className="mt-3 space-y-2 text-zinc-700">
              <li className="flex gap-2"><span className="text-[#16A34A] font-bold">•</span><span><strong>Hours:</strong> 20 hours per week (with potential for more during busy periods).</span></li>
              <li className="flex gap-2"><span className="text-[#16A34A] font-bold">•</span><span><strong>Wages:</strong> Negotiable depending on experience.</span></li>
              <li className="flex gap-2"><span className="text-[#16A34A] font-bold">•</span><span><strong>Work-Life Balance:</strong> Designed to work around school hours (approx. 9:00 AM – 2:00 PM).</span></li>
              <li className="flex gap-2"><span className="text-[#16A34A] font-bold">•</span><span><strong>Location:</strong> Hybrid. Work from home for the bulk of your shift, with brief daily reporting to our Bowen factory (twice a day) for phone collection and drop-off.</span></li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl font-black tracking-tight">Key Responsibilities</h2>
            <ul className="mt-3 space-y-2 text-zinc-700">
              <li className="flex gap-2"><span className="text-[#16A34A] font-bold">•</span><span><strong>The Human Link:</strong> Act as the primary point of contact for leads and service requests filtered through our Virtual Assistant (Rosie).</span></li>
              <li className="flex gap-2"><span className="text-[#16A34A] font-bold">•</span><span><strong>Service Advising:</strong> Convert digital requests into workshop-ready jobs, communicating clearly with customers about timelines.</span></li>
              <li className="flex gap-2"><span className="text-[#16A34A] font-bold">•</span><span><strong>Tech Improvement:</strong> Help us sharpen our AI tools by providing feedback from real customer interactions.</span></li>
              <li className="flex gap-2"><span className="text-[#16A34A] font-bold">•</span><span><strong>Logistics:</strong> Manage the daily collection and handover of the workshop mobile and documentation.</span></li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl font-black tracking-tight">About Us</h2>
            <p className="mt-3 text-zinc-700 leading-relaxed">Nomad Caravan Repairs has become a household name for insurance repairs in North Queensland. After building a successful repair centre in Townsville, we have moved into our next phase as a completely independent company. We are aligned with all major insurance providers and our catchment spans from Townsville to Mackay.</p>
            <p className="mt-3 text-zinc-700 leading-relaxed">We consider ourselves a holistic employer. We understand that managing work, life, and family is a challenge, and we are willing to accommodate those challenges as long as the exchange provides genuine value to both parties.</p>
          </div>

          <div className="border-t border-zinc-200 pt-6">
            <h2 className="font-display text-2xl font-black tracking-tight">How to Apply</h2>
            <p className="mt-3 text-zinc-900 font-bold">A cover letter is more essential to us than a seven-page resume. We're time-poor.</p>
            <p className="mt-2 text-zinc-700">Please use the form below to upload your resume and a brief cover letter.</p>
          </div>
        </article>

        <form onSubmit={submit} className="bg-white border border-zinc-200 p-6 sm:p-8 space-y-6 mt-6" data-testid="jobs-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Your name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="jobs-name"
                className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-[#16A34A]"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="jobs-email"
                className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-[#16A34A]"
              />
            </div>
          </div>

          <FileField
            label="Resume *"
            hint="PDF or Word document"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            file={resume}
            onChange={(f) => setResume(f)}
            testid="jobs-resume"
          />
          <FileField
            label="Cover letter (optional)"
            hint="PDF or Word document"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            file={cover}
            onChange={(f) => setCover(f)}
            testid="jobs-cover"
          />

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Photos (optional)</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setPhotos(e.target.files)}
              data-testid="jobs-photos"
              className="mt-2 w-full text-sm file:mr-4 file:py-2.5 file:px-4 file:border-0 file:bg-zinc-950 file:text-white file:font-semibold file:cursor-pointer border border-zinc-300 px-2 py-2"
            />
            {photos.length > 0 && <p className="text-xs text-zinc-500 mt-1">{photos.length} photo(s) selected</p>}
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="jobs-submit"
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#002075] disabled:opacity-60 inline-flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit application"}
          </button>
        </form>
      </section>
    </main>
  );
}

function FileField({ label, hint, accept, file, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</label>
      <div className="mt-2 flex items-center gap-3">
        <label className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 cursor-pointer hover:border-[#16A34A] text-sm font-semibold">
          <FileText className="w-4 h-4" /> Choose file
          <input type="file" accept={accept} onChange={(e) => onChange(e.target.files[0] || null)} data-testid={testid} className="hidden" />
        </label>
        <span className="text-sm text-zinc-600 truncate">{file ? file.name : hint}</span>
      </div>
    </div>
  );
}
